/**
 * Office startup, kept here so nothing else has to touch the `Office` global —
 * the same rule that applies to every other Office.js call.
 */
export interface BootstrapOptions {
  /**
   * How long to wait for `Office.onReady` before mounting anyway.
   *
   * `Office.onReady` never resolves when the page is opened in a plain browser,
   * and can hang inside Office when the host script is blocked by a proxy or an
   * enterprise policy. Either way the author must not be left looking at a white
   * pane, so the UI mounts regardless and reports that it is not in PowerPoint.
   */
  readonly timeoutMs?: number;
}

export interface BootstrapResult {
  /** True when Office signalled ready; false when we mounted on the timeout. */
  readonly officeReady: boolean;
  readonly host?: string;
  readonly platform?: string;
}

export async function whenOfficeReady(options: BootstrapOptions = {}): Promise<BootstrapResult> {
  const timeoutMs = options.timeoutMs ?? 3000;

  if (typeof Office === 'undefined' || typeof Office.onReady !== 'function') {
    return { officeReady: false };
  }

  const ready = Office.onReady().then((info) => ({
    officeReady: true,
    host: info?.host === undefined || info.host === null ? undefined : String(info.host),
    platform:
      info?.platform === undefined || info.platform === null ? undefined : String(info.platform),
  }));

  const timeout = new Promise<BootstrapResult>((resolve) => {
    setTimeout(() => resolve({ officeReady: false }), timeoutMs);
  });

  try {
    return await Promise.race([ready, timeout]);
  } catch {
    return { officeReady: false };
  }
}
